import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useLeadClock } from './LeadClockProvider';
import { AnimatedNumber } from './AnimatedNumber';
const LAYOUT_ID = 'lead-clock-card';
export function LeadClockExpanded() {
  const {
    elapsed,
    probability,
    status,
    theme,
    citation,
    interpretation,
    timerLabel,
    speed,
    setSpeed,
    reset,
    resetPulse
  } = useLeadClock();
  const reduceMotion = useReducedMotion();
  const [showSubtitle, setShowSubtitle] = useState(true);
  const [showCitation, setShowCitation] = useState(false);
  // Subtitle fades after 3s or on first scroll
  useEffect(() => {
    const t = window.setTimeout(() => setShowSubtitle(false), 3000);
    const onScroll = () => setShowSubtitle(false);
    window.addEventListener('scroll', onScroll, {
      once: true,
      passive: true
    });
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);
  return <motion.div layoutId={LAYOUT_ID} transition={reduceMotion ? {
    duration: 0
  } : {
    type: 'spring',
    stiffness: 240,
    damping: 28,
    mass: 0.9
  }} className="relative border border-gray-300 rounded-2xl bg-white overflow-hidden shadow-sm" data-id="element-317">
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 gap-3 sm:gap-4 flex-wrap" data-id="element-318">
        <div className="flex items-center gap-2 shrink-0" data-id="element-319">
          <PulsingDot color={theme.dot} reduceMotion={!!reduceMotion} data-id="element-320" />
          <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-600 whitespace-nowrap" data-id="element-321">
            What if this were your lead?
          </span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0" data-id="element-322">
          <SpeedSelector speed={speed} setSpeed={setSpeed} data-id="element-323" />
          <button type="button" onClick={reset} className="text-[11px] uppercase tracking-[0.15em] font-semibold text-gray-500 hover:text-gray-900 transition-colors" data-id="element-324">
            ↺ Reset
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 sm:px-6 py-5 sm:py-6 flex flex-col gap-5 sm:gap-6" data-id="element-325">
        {/* Timer + probability row */}
        <div className="flex items-start justify-between gap-4 sm:gap-6" data-id="element-326">
          <div className="flex flex-col gap-1 min-w-0" data-id="element-327">
            <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-gray-500" data-id="element-328">
              Waiting
            </span>
            <span className="text-4xl sm:text-5xl font-mono font-semibold tabular-nums text-gray-900 leading-none" key={`timer-${resetPulse}`} data-id="element-329">
              {timerLabel}
            </span>
          </div>

          <div className="flex flex-col items-end gap-1 relative min-w-0" data-id="element-330">
            <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-gray-500" data-id="element-331">
              Chance you close
            </span>
            <button type="button" onMouseEnter={() => setShowCitation(true)} onMouseLeave={() => setShowCitation(false)} onFocus={() => setShowCitation(true)} onBlur={() => setShowCitation(false)} className="flex items-baseline gap-0.5 leading-none cursor-help focus:outline-none" aria-describedby="lead-clock-citation" data-id="element-332">
              <AnimatedNumber value={probability} duration={500} className={`text-4xl sm:text-5xl font-mono font-semibold tabular-nums ${theme.text} transition-colors duration-500`} data-id="element-333" />
              <span className={`text-xl sm:text-2xl font-mono font-semibold ${theme.text} transition-colors duration-500`} data-id="element-334">
                %
              </span>
            </button>

            {/* Citation tooltip */}
            <div id="lead-clock-citation" role="tooltip" className={`absolute top-full right-0 mt-2 px-3 py-1.5 rounded-md bg-gray-900 text-gray-300 font-mono text-[11px] whitespace-nowrap shadow-lg pointer-events-none z-10 transition-opacity duration-150 ${showCitation ? 'opacity-100' : 'opacity-0'}`} data-id="element-335">
              {citation}
            </div>
          </div>
        </div>

        {/* Decay bar + status */}
        <div className="flex flex-col gap-2" data-id="element-336">
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-337">
            <motion.div className={`h-full ${theme.bg} rounded-full transition-colors duration-500`} animate={{
            width: `${probability}%`
          }} transition={reduceMotion ? {
            duration: 0
          } : {
            duration: elapsed > 300 ? 0.6 : 0.4,
            ease: elapsed > 300 ? 'easeIn' : 'easeOut'
          }} data-id="element-338" />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-3 sm:min-h-[1.25rem]" data-id="element-339">
            <AnimatePresence mode="wait" initial={false} data-id="element-340">
              <motion.span key={`status-${status}`} initial={reduceMotion ? {
              opacity: 0
            } : {
              opacity: 0,
              y: 4
            }} animate={reduceMotion ? {
              opacity: 1
            } : {
              opacity: 1,
              y: 0
            }} exit={reduceMotion ? {
              opacity: 0
            } : {
              opacity: 0,
              y: -4
            }} transition={{
              duration: 0.28,
              ease: 'easeOut'
            }} className={`text-xs font-mono font-semibold tracking-wider ${theme.text} transition-colors duration-500 whitespace-nowrap`} data-id="element-341">
                {status}
              </motion.span>
            </AnimatePresence>
            <AnimatePresence mode="wait" initial={false} data-id="element-342">
              <motion.span key={`interp-${status}`} initial={reduceMotion ? {
              opacity: 0
            } : {
              opacity: 0,
              y: 4
            }} animate={reduceMotion ? {
              opacity: 1
            } : {
              opacity: 1,
              y: 0
            }} exit={reduceMotion ? {
              opacity: 0
            } : {
              opacity: 0,
              y: -4
            }} transition={{
              duration: 0.28,
              ease: 'easeOut'
            }} className={`text-xs text-left sm:text-right leading-snug ${theme.text} transition-colors duration-500 sm:max-w-[65%]`} data-id="element-343">
                {interpretation}
              </motion.span>
            </AnimatePresence>
          </div>
        </div>

        {/* Subtitle (fades after 3s or scroll) */}
        <div className="h-4" data-id="element-344">
          <span className={`text-[11px] italic text-gray-500 transition-opacity duration-500 ${showSubtitle ? 'opacity-100' : 'opacity-0'}`} data-id="element-345">
            Calibrated against 132K real lead-response campaigns
          </span>
        </div>
      </div>
    </motion.div>;
}
const SPEEDS: Array<1 | 2 | 5 | 10> = [1, 2, 5, 10];
function SpeedSelector({
  speed,
  setSpeed
}: {
  speed: 1 | 2 | 5 | 10;
  setSpeed: (s: 1 | 2 | 5 | 10) => void;
}) {
  return <div role="group" aria-label="Clock speed" className="hidden sm:inline-flex items-center gap-0.5 p-0.5 rounded-md border border-gray-200 bg-gray-50" data-id="element-346">
      <span className="text-[9px] uppercase tracking-[0.15em] font-semibold text-gray-400 px-1.5" data-id="element-347">
        Speed
      </span>
      {SPEEDS.map(s => <button key={s} type="button" onClick={() => setSpeed(s)} aria-pressed={speed === s} className={`text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded transition-colors ${speed === s ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`} data-id="element-348">
          {s}×
        </button>)}
    </div>;
}
function PulsingDot({
  color,
  reduceMotion
}: {
  color: string;
  reduceMotion: boolean;
}) {
  return <span className="relative inline-flex w-2.5 h-2.5" data-id="element-349">
      {!reduceMotion && <span className={`absolute inset-0 rounded-full ${color} opacity-60 animate-ping transition-colors duration-500`} data-id="element-350" />}
      <span className={`relative inline-flex w-2.5 h-2.5 rounded-full ${color} transition-colors duration-500`} data-id="element-351" />
    </span>;
}